import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Multiselect } from '../components';
import { Input } from '../components/Input';
import { TOAST_DURATION, USER_ROLE } from '../lib/constants';
import { useCreateUserMutation, useProjectsQuery } from '../lib/queries';
import { NewUserForm, NewUserSchema } from '../lib/schemas';
import { PATHS } from '../lib/paths';
import LanguageSelector from '../components/LanguageSelector';

export default function NewUserPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { register, watch, handleSubmit, formState, setValue, reset } = useForm<NewUserForm>({
    mode: 'all',
    resolver: zodResolver(NewUserSchema),
  });

  const language = watch('language', '');

  const project = watch('project', []);
  const projectsQuery = useProjectsQuery();

  const projectList = projectsQuery.data ? projectsQuery.data?.projects : [];

  const createUserMutation = useCreateUserMutation();

  const onSubmit = async (data: NewUserForm) => {
    createUserMutation.mutate(
      { ...data, project: data.project?.map((p) => p.id) ?? [] },
      {
        onSuccess: ({ user }) => {
          toast.success(t('newUser.created', { project: user.name }), {
            position: 'top-center',
            duration: TOAST_DURATION,
          });
          navigate(PATHS.homePath());
        },
        onError: (error) => {
          const err = error as Error;
          navigate(PATHS.errorPath(err.message));
        },
      },
    );
  };

  useEffect(
    () => {
      setValue('role', USER_ROLE.CLIENT);
      setValue('project', []);
      return () => reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <main className="grid gap-4 w-700 mx-auto">
      <h1 className="text-hero font-bold">{t('newUser.title')}</h1>
      <form method="post" className="grid gap-2 mt-10" onSubmit={handleSubmit(onSubmit)}>
        <LanguageSelector
          onChange={(e) => {
            setValue('language', e.target.value, { shouldValidate: true });
          }}
          label={t('newUser.language')}
          value={language}
          required
        />
        <Input
          type="text"
          {...register('name', { required: true })}
          label={t('newUser.name')}
          placeholder="name"
          error={formState.errors.name?.message?.toString()}
          required
        />
        <Input
          type="text"
          {...register('email', { required: true })}
          label={t('common.email')}
          placeholder="email"
          error={formState.errors.email?.message?.toString()}
          required
        />

        <Multiselect
          label="newUser.project"
          required
          options={projectList}
          show={(p) => {
            return p.name;
          }}
          value={project}
          error={t(formState.errors.project?.message?.toString() ?? '')}
          onChange={(p) => setValue('project', p, { shouldValidate: true })}
        />

        <div className="flex justify-end items-center gap-2 mt-4">
          <button className="primary" onClick={() => navigate(PATHS.homePath())} type="button">
            {t('common.cancel')}
          </button>
          <button className="primary-fill" type="submit" disabled={!formState.isValid}>
            {t('newUser.create')}
          </button>
        </div>
      </form>
    </main>
  );
}
